<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import { Dialog, DialogContent } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { Textarea } from '@/components/ui/textarea'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
  import { Label } from '@/components/ui/label'
  import moment from 'moment-timezone'
  import {
    AppointmentProposalsService,
    AppointmentSettingsService,
    LocationTypeEnum
  } from '@/types/generated'
  import { AccountsService, AppointmentsService } from '@/types/generated/services.gen'
  import { genericErrorToast } from '@/lib/toasts'
  import { useToast } from '@/components/ui/toast'

  // Add type declaration for Cronofy
  declare global {
    interface Window {
      CronofyElements: any
    }
  }

  interface Service {
    uuid: string
    name: string
    description: string
    calendars: Array<{
      id: number
      name: string
      calendar_id: string
      availability_query: any
      uuid: string
    }>
    location_types: Array<{
      name: string
      value: LocationTypeEnum
    }>
    availability_query: any
  }

  const props = defineProps<{
    open: boolean
    case: {
      services: Service[]
      uuid: string
    }
  }>()

  const emit = defineEmits<{
    'update:open': [value: boolean]
    'appointment-created': []
  }>()

  const { toast } = useToast()

  const selectedTab = ref('link')
  const selectedService = ref('')
  const selectedCalendar = ref('')
  const selectedAppointmentType = ref<LocationTypeEnum>('ONLINE')
  const restrictAvailability = ref(false)
  const additionalInfo = ref('')
  const appointmentDate = ref('')

  // Add state for working hours
  const startOfWorkingDay = ref(9)
  const endOfWorkingDay = ref(17)
  const workingDays = ref([0, 1, 2, 3, 4])
  const limitToAvailabilityRule = ref(true)

  const cronofyLoaded = ref(false)
  const isSubmitting = ref(false)

  // Computed properties for select options
  const serviceOptions = computed(() => {
    return (
      props.case?.services?.map((service) => ({
        value: service.uuid,
        title: service.name
      })) || []
    )
  })

  const selectedServiceData = computed(() => {
    return props.case?.services?.find((service) => service.uuid === selectedService.value)
  })

  const calendarOptions = computed(() => {
    if (!selectedServiceData.value) return []
    return selectedServiceData.value.calendars.map((calendar) => ({
      value: calendar.uuid,
      title: calendar.name
    }))
  })

  const appointmentTypeOptions = computed(() => {
    if (!selectedServiceData.value) return []
    return selectedServiceData.value.location_types.map((type) => ({
      value: type.value,
      title: type.name
    }))
  })

  // Reset dependent fields when service changes
  const handleServiceChange = () => {
    selectedCalendar.value = ''
    selectedAppointmentType.value = 'ONLINE'
  }

  const closeModal = () => {
    emit('update:open', false)
  }

  const sendBookingLink = async () => {
    if (isSubmitting.value) return
    isSubmitting.value = true
    try {
      await AppointmentProposalsService.appointmentProposalsCreate({
        requestBody: {
          case_uuid: props.case.uuid,
          service_uuid: selectedService.value,
          calendar_uuid: selectedCalendar.value,
          start: appointmentDate.value,
          message: additionalInfo.value,
          location_type: selectedAppointmentType.value
        }
      })

      emit('appointment-created')
      closeModal()
    } catch (error: any) {
      console.error('Error creating appointment proposal:', error)

      const errorMessage = error?.body?.detail || error?.detail

      if (errorMessage) {
        toast({
          title: 'Fehler',
          description: errorMessage,
          variant: 'destructive'
        })
      } else {
        genericErrorToast()
      }

      closeModal()
    } finally {
      isSubmitting.value = false
    }
  }

  // Fetch organization settings
  const fetchOrganisationSettings = async () => {
    try {
      const response = await AppointmentSettingsService.appointmentSettingsRetrieve()
      const currentTimeInGermany = moment().tz('Europe/Berlin')
      const utcOffsetWithGermanyHours = currentTimeInGermany.utcOffset() / 60
      startOfWorkingDay.value =
        response.start_of_working_day.split(':')[0] - utcOffsetWithGermanyHours
      endOfWorkingDay.value = response.end_of_working_day.split(':')[0] - utcOffsetWithGermanyHours
      workingDays.value = response.working_days
    } catch (error) {
      console.error('Error fetching organization settings: ', error)
    }
  }

  const initializeCronofy = async (
    selectedServiceObj: Service,
    selectedCalendarObj: any,
    token: string,
    eleId: string,
    newLimitToAvailabilityRule: boolean
  ) => {
    const deepClone = (obj: any) => JSON.parse(JSON.stringify(obj))
    const availabilityQuery = {
      ...deepClone(selectedCalendarObj.availability_query),
      ...deepClone(selectedServiceObj.availability_query)
    }

    const start = getNextAvailableHour()
    availabilityQuery.query_periods[0].start = start.toISOString()

    if (availabilityQuery.participants && availabilityQuery.participants.length > 0) {
      availabilityQuery.participants.forEach((participant: any) => {
        if (participant.members && participant.members.length > 0) {
          participant.members.forEach((member: any) => {
            member.managed_availability = true
            const availabilityRule = []
            if (newLimitToAvailabilityRule) {
              // When limiting to availability rules, exclude workhours rules
              const filteredRules = member.availability_rule_ids.filter(
                (rule: string) => !rule.includes('workhours')
              )
              availabilityRule.push(filteredRules)
            } else {
              // When not limiting, only include workhours rules
              const filteredRules = member.availability_rule_ids.filter((rule: string) =>
                rule.includes('workhours')
              )
              availabilityRule.push(filteredRules)
            }
            member.availability_rule_ids = availabilityRule.flat()
          })
        }
      })
    }

    window.CronofyElements.DateTimePicker({
      locale: 'de',
      config: {
        mode: 'no_confirm',
        origin: window.location.origin
      },
      element_token: token || undefined,
      demo: !token,
      data_center: 'de',
      target_id: eleId,
      availability_query: availabilityQuery,
      styles: {
        colors: {
          black: '#000000',
          white: '#ffffff',
          neutralDark: '#6f6f6f',
          neutral: '#999999',
          neutralLight: '#e4ebf2',
          buttonConfirm: '#7A6CFF75',
          buttonConfirmText: '#000000',
          buttonHover: '#7A6CFF75',
          buttonText: '#000000',
          buttonTextHover: '#000000',
          buttonActive: '#7A6CFF',
          buttonActiveText: '#ffffff'
        }
      },
      callback: function (event: any) {
        if (event.notification?.slot) {
          appointmentDate.value = event.notification.slot.start
        }
      }
    })
  }

  onMounted(() => {
    fetchOrganisationSettings()

    // Load Cronofy script if not already loaded
    if (!document.getElementById('cronofy-script')) {
      const script = document.createElement('script')
      script.id = 'cronofy-script'
      script.src = 'https://elements.cronofy.com/js/CronofyElements.v1.61.1.js'
      script.async = true
      script.onload = () => {
        cronofyLoaded.value = true
      }
      document.head.appendChild(script)
    } else {
      // If script is already loaded, set cronofyLoaded to true
      cronofyLoaded.value = window.CronofyElements !== undefined
    }
  })

  // Cronofy integration
  const getCronofyElementId = (calendarId: string, serviceId: string) => {
    // Use numeric IDs for DOM element ID
    const eleId = `cronofy-date-time-picker__${calendarId.replace(/\W/g, '')}_${serviceId.replace(/\W/g, '')}`
    return eleId
  }

  //   const getCronofyElementId = (calendarId: number, serviceId: number) => {
  //     return 'cronofy-date-time-picker__' + calendarId + '__' + serviceId;
  // };

  function getNextAvailableHour() {
    const nextAvailableHour = new Date()
    nextAvailableHour.setHours(nextAvailableHour.getHours() + 1)
    nextAvailableHour.setMinutes(0)
    nextAvailableHour.setSeconds(0)
    nextAvailableHour.setMilliseconds(0)
    return nextAvailableHour
  }

  const getAvailabilityToken = async (accountUuid: string) => {
    try {
      const response = await AccountsService.accountsAvailabilityTokenRetrieve({
        uuid: accountUuid
      })
      return response.token
    } catch (error) {
      console.error('Error getting availability token:', error)
      return null
    }
  }

  const createAppointment = async () => {
    if (
      !selectedService.value ||
      !selectedCalendar.value ||
      !selectedAppointmentType.value ||
      !appointmentDate.value ||
      isSubmitting.value
    ) {
      return
    }
    isSubmitting.value = true

    try {
      const selectedServiceObj = props.case?.services?.find((s) => s.uuid === selectedService.value)
      const selectedCalendarObj = selectedServiceObj?.calendars.find(
        (c) => c.uuid === selectedCalendar.value
      )
      if (!selectedServiceObj || !selectedCalendarObj) return

      const requestBody = {
        service: selectedServiceObj.uuid,
        calendar: selectedCalendarObj.uuid,
        case: props.case.uuid,
        location_types: selectedAppointmentType.value,
        start: appointmentDate.value
      }

      const response = await AppointmentsService.appointmentsCreate({
        requestBody
      })

      emit('appointment-created')
      closeModal()
    } catch (error: any) {
      console.error('Error creating appointment:', error)

      const errorMessage = error?.body?.detail || error?.detail

      if (errorMessage) {
        toast({
          title: 'Fehler',
          description: errorMessage,
          variant: 'destructive'
        })
      } else {
        genericErrorToast()
      }

      closeModal()
    } finally {
      isSubmitting.value = false
    }
  }

  const canCreateAppointment = computed(() => {
    return (
      selectedService.value &&
      selectedCalendar.value &&
      selectedAppointmentType.value &&
      appointmentDate.value
    )
  })

  watch(
    [
      selectedService,
      selectedCalendar,
      selectedTab,
      selectedAppointmentType,
      limitToAvailabilityRule,
      cronofyLoaded
    ],
    async (
      [newService, newCalendar, newTab, newLocationType, newLimitToAvailabilityRule],
      [oldService]
    ) => {
      // Reset calendar and location type when service changes
      if (newService !== oldService) {
        selectedCalendar.value = ''
        selectedAppointmentType.value = 'ONLINE'
        appointmentDate.value = ''
      }

      // Clear and reinitialize Cronofy
      const cronofyHolder = document.getElementById('cronofy-holder')
      if (cronofyHolder) {
        cronofyHolder.innerHTML = ''
      }

      if (
        newService &&
        newCalendar &&
        newLocationType &&
        newTab === 'manual' &&
        cronofyLoaded.value
      ) {
        const selectedServiceObj = props.case?.services?.find((s) => s.uuid === newService)
        const selectedCalendarObj = selectedServiceObj?.calendars.find(
          (c) => c.uuid === newCalendar
        )
        if (!selectedServiceObj || !selectedCalendarObj) return

        console.log('selectedCalendarObj', selectedCalendarObj)
        try {
          // Get availability token using calendar account UUID
          let token = null
          try {
            token = await getAvailabilityToken(selectedCalendarObj.account.uuid)
          } catch (error) {
            console.warn('Failed to get availability token, falling back to demo mode:', error)
          }

          const eleId = getCronofyElementId(selectedCalendarObj.uuid, selectedServiceObj.uuid)
          document.getElementById('cronofy-holder').innerHTML =
            `<div class='cronofy-date-time-picker' id='${eleId}'></div>`

          await initializeCronofy(
            selectedServiceObj,
            selectedCalendarObj,
            token,
            eleId,
            newLimitToAvailabilityRule
          )
        } catch (error) {
          console.error('Error initializing Cronofy:', error)
        }
      }
    },
    { deep: true }
  )
</script>

<template>
  <Dialog
    :open="open"
    class=""
    @update:open="(value) => emit('update:open', value)"
  >
    <DialogContent class="max-h-[90vh] w-full max-w-3xl overflow-y-auto p-0">
      <div class="p-6">
        <!-- Header -->
        <div class="mb-6 flex items-center justify-between">
          <h2 class="text-xl font-semibold">Termin hinzufügen</h2>
        </div>

        <!-- Tabs -->
        <RadioGroup
          v-model="selectedTab"
          class="mb-6 grid grid-cols-2 gap-4"
        >
          <div
            class="group relative rounded-lg border p-3"
            :class="[
              selectedTab === 'link'
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
          >
            <Label class="flex cursor-pointer flex-col gap-0.5">
              <RadioGroupItem
                value="link"
                data-cy="bookingLinkTab"
                class="absolute left-3 top-3 h-4 w-4 rounded-full border border-purple-500 text-purple-500
                  focus:ring-purple-500"
              />
              <div class="font-semibold">Buchungslink senden</div>
              <div class="text-sm leading-tight text-gray-600">
                Senden Sie einen Link an den Mandanten mit dem er sich selbst einen Termin (nach
                Verfügbarkeit) buchen kann.
              </div>
            </Label>
          </div>

          <div
            class="group relative rounded-lg border p-3"
            :class="[
              selectedTab === 'manual'
                ? 'border-purple-500 bg-purple-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            ]"
          >
            <Label class="flex cursor-pointer flex-col gap-0.5">
              <RadioGroupItem
                value="manual"
                data-cy="manualEntryTab"
                class="absolute left-3 top-3 h-4 w-4 rounded-full border border-purple-500 text-purple-500
                  focus:ring-purple-500"
              />
              <div class="font-semibold">Termin eintragen</div>
              <div class="text-sm leading-tight text-gray-600">
                Wollen Sie einen Termin vorgeben oder haben Sie bereits einen Termin abgesprochen?
                Dann tragen Sie diesen hier ein.
              </div>
            </Label>
          </div>
        </RadioGroup>

        <!-- Form Fields -->
        <div class="space-y-4">
          <div>
            <Label class="mb-1.5 block">Leistung</Label>
            <SelectField
              v-model="selectedService"
              :options="serviceOptions"
              name="service"
              placeholder="Nicht ausgewählt"
              :allow-unselect="false"
              class="w-full"
              @update:model-value="handleServiceChange"
            />
          </div>

          <div>
            <Label class="mb-1.5 block">
              Kalender
              <span
                v-if="!selectedService"
                class="text-sm text-gray-500"
              >
                (wählen Sie die Leistung erst)
              </span>
            </Label>
            <SelectField
              v-model="selectedCalendar"
              :options="calendarOptions"
              name="calendar"
              placeholder="Nicht ausgewählt"
              :allow-unselect="false"
              :disabled="!selectedService"
              class="w-full"
            />
          </div>

          <template v-if="selectedTab === 'link'">
            <!-- Two columns layout -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <Label class="mb-1.5 block">Termin buchbar ab dem</Label>
                <input
                  v-model="appointmentDate"
                  type="datetime-local"
                  data-cy="appointmentDateInput"
                  class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm
                    ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium
                    placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2
                    focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div>
                <Label class="mb-1.5 block">
                  Art des Termins
                  <span
                    v-if="!selectedService"
                    class="text-sm text-gray-500"
                  >
                    (wählen Sie die Leistung erst)
                  </span>
                </Label>
                <SelectField
                  v-model="selectedAppointmentType"
                  :options="appointmentTypeOptions"
                  name="appointment-type"
                  placeholder="Nicht ausgewählt"
                  :allow-unselect="false"
                  :disabled="!selectedService"
                  class="w-full"
                />
              </div>
            </div>
          </template>

          <template v-if="selectedTab === 'manual'">
            <div>
              <Label class="mb-1.5 block">
                Art des Termins
                <span
                  v-if="!selectedService"
                  class="text-sm text-gray-500"
                >
                  (wählen Sie die Leistung erst)
                </span>
              </Label>
              <SelectField
                v-model="selectedAppointmentType"
                :options="appointmentTypeOptions"
                name="appointment-type"
                placeholder="Nicht ausgewählt"
                :allow-unselect="false"
                :disabled="!selectedService"
                class="w-full"
              />
            </div>

            <CheckboxField
              v-model="limitToAvailabilityRule"
              name="limitToAvailabilityRule"
              label="Auf Verfügbarkeiten beschränken"
            />

            <!-- Cronofy Element Container -->
            <div
              v-if="selectedService && selectedCalendar"
              id="cronofy-holder"
              class="mt-4"
            ></div>
          </template>

          <div v-if="selectedTab === 'link'">
            <Label class="mb-1.5 block">Zusätzliche Informationen</Label>
            <Textarea
              v-model="additionalInfo"
              data-cy="appointmentMessage"
              placeholder="Geben Sie hier zusätzliche Informationen ein..."
              class="w-full"
            />
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex border-t p-4">
        <Button
          variant="secondary"
          class="mr-auto"
          @click="closeModal"
        >
          Abbrechen
        </Button>

        <Button
          v-if="selectedTab === 'link'"
          variant="default"
          data-cy="sendBookingLinkBtn"
          :disabled="
            !selectedService ||
            !selectedCalendar ||
            !selectedAppointmentType ||
            !appointmentDate ||
            isSubmitting
          "
          @click="sendBookingLink"
        >
          Buchungslink senden
        </Button>
        <Button
          v-else
          variant="default"
          data-cy="createAppointmentBtn"
          :disabled="!canCreateAppointment || isSubmitting"
          @click="createAppointment"
        >
          Termin eintragen
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
